import React, { useEffect, useRef, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Checkbox,
    CircularProgress,
    FormControlLabel,
    FormGroup,
    Grid,
    IconButton,
    InputAdornment,
    Paper,
    Stack,
    TextField,
    Typography
} from '@mui/material';
import {
    Mic as MicIcon,
    MicOff as MicOffIcon,
    Send as SendIcon,
    PersonSearch as PersonSearchIcon,
    Draw as DrawIcon,
    Clear as ClearIcon,
    ArrowBack as ArrowBackIcon,
    CameraAlt as CameraAltIcon,
    Delete as DeleteIcon
} from '@mui/icons-material';
import dayjs from 'dayjs';
import { useSearchParams, useNavigate } from 'react-router-dom';
import useDictation from '../../hooks/useDictation';
import SelectEmployeesAndTempsModal from '../jha/SelectEmployeesAndTempsModal';
import SignatureModalWithAuto from '../common/SignatureModalWithAuto';
import CameraCapture from '../shared/CameraCapture';
import unifiedFormService, { getByUUID } from '../../services/unifiedFormService';
import { hasSignatureContent } from '../../utils/utils';

const TYPE = 'employee_warning_notice';

const WARNING_REASONS = [
    { key: 'notWearingPPE', label: 'Not Wearing PPE' },
    { key: 'beingLate', label: 'Being Late For Work' },
    { key: 'insubordination', label: 'Insubordination' },
    { key: 'poorSafetyHabits', label: 'Poor Safety Habits' },
    { key: 'beingUnderInfluence', label: 'Being Under the Influence' },
    { key: 'violationOfPolicy', label: 'Violation of Company Policy or Procedures' },
    { key: 'unsatisfactoryPerformance', label: 'Unsatisfactory Job Performance' },
    { key: 'failureToFollowInstructions', label: 'Failure to Follow Supervisor\'s Instructions' },
    { key: 'unsafeEquipmentOperation', label: 'Unsafe Operation of Equipment' },
    { key: 'abuseOfEquipment', label: 'Abuse of Equipment' },
    { key: 'excessiveAbsences', label: 'Excessive/Unexcused Absences' },
    { key: 'otherProcedures', label: 'Other Procedures' }
];

export default function EmployeeWarningNoticeForm() {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const [form, setForm] = useState({
        uuid: '',
        date: dayjs().format('YYYY-MM-DD'),
        employeeName: '',
        employeeId: '',
        employeeType: '',
        warningReasons: {},
        detailsOfEvent: '',
        warningNumber: { warning1: false, warning2: false, warning3: false },
        actionTaken: '',
        objectionText: '',
        photos: [],
        supervisorSignature: '',
        supervisorSignatureDate: null,
        employeeSignature: '',
        employeeSignatureDate: null
    });
    const [errors, setErrors] = useState({});
    const [saving, setSaving] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [success, setSuccess] = useState('');
    const [errorMsg, setErrorMsg] = useState('');
    const [employeeModalOpen, setEmployeeModalOpen] = useState(false);
    const [supervisorSigModalOpen, setSupervisorSigModalOpen] = useState(false);
    const [employeeSigModalOpen, setEmployeeSigModalOpen] = useState(false);
    const [cameraOpen, setCameraOpen] = useState(false);
    const supervisorSigPadRef = useRef(null);
    const employeeSigPadRef = useRef(null);

    // Dictation
    const { isSupported: speechSupported, isListening, start: startDictation, stop: stopDictation } = useDictation({ lang: 'en-US' });
    const [dictationTarget, setDictationTarget] = useState(null);

    // Load existing draft
    useEffect(() => {
        (async () => {
            const uuid = searchParams.get('uuid');
            if (!uuid) return;
            try {
                const rec = await getByUUID(uuid);
                if (rec) {
                    const savedData = rec.payload || {};
                    // Only mark as submitted if IndexedDB record status is submitted
                    if (rec.status === 'submitted') {
                        setSubmitted(true);
                    }
                    // For photos loaded from IndexedDB, ensure they have proper display URLs
                    let photos = savedData.photos || [];
                    if (photos.length > 0) {
                        photos = photos.map(photo => {
                            if (!photo.dataUrl && photo.filename) {
                                photo.url = `/api/forms/employee_warning_notice/${rec.uuid || uuid}/photo/${photo.filename}`;
                            }
                            return photo;
                        });
                    }

                    setForm({
                        uuid: rec.uuid || uuid,
                        date: savedData.date || dayjs().format('YYYY-MM-DD'),
                        employeeName: savedData.employeeName || '',
                        employeeId: savedData.employeeId || '',
                        employeeType: savedData.employeeType || '',
                        warningReasons: savedData.warningReasons || {},
                        detailsOfEvent: savedData.detailsOfEvent || '',
                        warningNumber: savedData.warningNumber || { warning1: false, warning2: false, warning3: false },
                        actionTaken: savedData.actionTaken || '',
                        objectionText: savedData.objectionText || '',
                        photos,
                        supervisorSignature: savedData.supervisorSignature || '',
                        supervisorSignatureDate: savedData.supervisorSignatureDate || null,
                        employeeSignature: savedData.employeeSignature || '',
                        employeeSignatureDate: savedData.employeeSignatureDate || null
                    });
                }
            } catch (e) {
                console.error('Error loading form:', e);
            }
        })();
    }, [searchParams]);

    // Autosave
    const saveTimer = useRef(null);
    const queueSave = (data) => {
        if (submitted) return;
        if (saveTimer.current) clearTimeout(saveTimer.current);
        saveTimer.current = setTimeout(async () => {
            try {
                const saveData = {
                    ...data,
                    type: TYPE,
                    status: 'draft',
                    updatedAt: new Date().toISOString()
                };
                const { uuid } = await unifiedFormService.saveLocal(TYPE, saveData, data.uuid);
                if (!data.uuid && uuid) {
                    setForm(prev => ({ ...prev, uuid }));
                }
            } catch (e) {
                console.error('Autosave failed:', e);
            }
        }, 1000);
    };

    const updateField = (field, value) => {
        const updated = { ...form, [field]: value };
        setForm(updated);
        queueSave(updated);
    };

    const validate = (data) => {
        const e = {};
        if (!data.employeeName) e.employeeName = 'Employee is required';
        if (!data.detailsOfEvent || data.detailsOfEvent.length < 10) e.detailsOfEvent = 'Details required (min 10 characters)';
        if (!data.actionTaken || data.actionTaken.length < 10) e.actionTaken = 'Action taken required (min 10 characters)';
        if (!data.supervisorSignature) e.supervisorSignature = 'Supervisor signature required';
        if (!data.employeeSignature) e.employeeSignature = 'Employee signature required';

        const selectedReasons = Object.values(data.warningReasons || {}).filter(Boolean);
        if (selectedReasons.length === 0) e.warningReasons = 'Select at least one warning reason';

        const selectedWarnings = Object.values(data.warningNumber || {}).filter(Boolean);
        if (selectedWarnings.length === 0) e.warningNumber = 'Select warning number';

        return e;
    };

    const handleSubmit = async () => {
        if (submitted) {
            setErrorMsg('This form has already been submitted');
            return;
        }

        setErrorMsg('');
        setSuccess('');
        const e = validate(form);
        setErrors(e);
        if (Object.keys(e).length > 0) return;
        setSaving(true);
        try {
            // Add submitter info from localStorage
            const firstName = localStorage.getItem('firstName') || '';
            const lastName = localStorage.getItem('lastName') || '';
            const submittedByName = firstName && lastName ? `${firstName} ${lastName}` : (firstName || lastName || '');

            // Clean photos for submission
            const cleanedPhotos = (form.photos || []).map(photo => {
                if (photo.dataUrl) {
                    return { dataUrl: photo.dataUrl, description: photo.description || '', timestamp: photo.timestamp };
                }
                return { filename: photo.filename, description: photo.description || '', timestamp: photo.timestamp };
            });

            const payload = {
                ...form,
                photos: cleanedPhotos,
                submittedByName
            };
            const res = await unifiedFormService.submit(TYPE, payload, form.uuid || undefined);

            // Only mark as submitted if API returned success
            const apiSuccess = !!(res && res.success === true);

            if (apiSuccess) {
                setSubmitted(true);
                setSuccess('Form submitted successfully! Redirecting...');
                if (res?.uuid) updateField('uuid', res.uuid);
                setTimeout(() => {
                    navigate('/forms/employee-warning-notice/list');
                }, 1500);
            } else if (res && res.queued) {
                // Queued for sync when online
                setSubmitted(true);
                setSuccess('Offline: Form queued for submission when online');
                if (res?.uuid && !form.uuid) updateField('uuid', res.uuid);
                setTimeout(() => {
                    navigate('/forms/employee-warning-notice/list');
                }, 1500);
            } else {
                // Failed - keep as draft
                setErrorMsg(res?.message || 'Submit failed');
            }
        } catch (e) {
            setErrorMsg('Submit failed: ' + (e.message || 'Unknown error'));
        } finally {
            setSaving(false);
        }
    };

    const startFieldDictation = async (fieldKey) => {
        setDictationTarget(fieldKey);
        await startDictation({
            onResult: (text) => {
                if (!text) return;
                updateField(fieldKey, (form[fieldKey] ? form[fieldKey] + ' ' : '') + text);
            },
            onEnd: () => setDictationTarget(null),
            onError: () => setDictationTarget(null),
            continuous: false,
            interimResults: false,
            safetyTimeoutMs: 15000,
            prime: true,
        });
    };

    const stopFieldDictation = () => {
        try { stopDictation(); } catch (e) { /* ignore */ }
        setDictationTarget(null);
        setTimeout(() => { try { stopDictation(); } catch (e) {} }, 150);
    };

    useEffect(() => {
        const kill = () => { try { stopDictation(); } catch (e) {} };
        window.addEventListener('pagehide', kill);
        document.addEventListener('visibilitychange', () => { if (document.hidden) kill(); });
        return () => {
            try { stopDictation(); } catch (e) {}
            window.removeEventListener('pagehide', kill);
        };
    }, [stopDictation]);

    const handleEmployeeSelect = (selected) => {
        if (!selected || selected.length === 0) return;
        const person = selected[0]; // Take first selected
        const employeeId = person._type === 'employee' ? (person.ee || person.EE || '') : (person.agency || person.Agency || '');
        const updated = {
            ...form,
            employeeName: person.FullName || '',
            employeeId,
            employeeType: person._type || ''
        };
        setForm(updated);
        queueSave(updated);
    };

    // Photo handlers
    const handlePhotoCapture = (photoData) => {
        const newPhoto = {
            dataUrl: photoData.dataUrl,
            url: photoData.dataUrl,
            name: photoData.name,
            description: '',
            timestamp: photoData.timestamp || new Date().toISOString()
        };
        const updated = { ...form, photos: [...(form.photos || []), newPhoto] };
        setForm(updated);
        queueSave(updated);
        setCameraOpen(false);
    };

    const updatePhotoDescription = (index, desc) => {
        const updatedPhotos = (form.photos || []).map((photo, i) =>
            i === index ? { ...photo, description: desc } : photo
        );
        const updated = { ...form, photos: updatedPhotos };
        setForm(updated);
        queueSave(updated);
    };

    const removePhoto = (index) => {
        const updated = { ...form, photos: (form.photos || []).filter((_, i) => i !== index) };
        setForm(updated);
        queueSave(updated);
    };

    const acceptSupervisorSignature = () => {
        if (hasSignatureContent(supervisorSigPadRef)) {
            const dataUrl = supervisorSigPadRef.current.toDataURL();
            const updated = {
                ...form,
                supervisorSignature: dataUrl,
                supervisorSignatureDate: new Date().toISOString()
            };
            setForm(updated);
            queueSave(updated);
            setSupervisorSigModalOpen(false);
        }
    };

    const acceptEmployeeSignature = () => {
        if (hasSignatureContent(employeeSigPadRef)) {
            const dataUrl = employeeSigPadRef.current.toDataURL();
            const updated = {
                ...form,
                employeeSignature: dataUrl,
                employeeSignatureDate: new Date().toISOString()
            };
            setForm(updated);
            queueSave(updated);
            setEmployeeSigModalOpen(false);
        }
    };

    return (
        <Box sx={{
            height: 'calc(100vh - 60px)',
            backgroundColor: '#f5f5f5',
            overflowY: 'auto',
            display: 'flex',
            flexDirection: 'column'
        }}>
            {/* Header */}
            <Paper sx={{ p: 1.5, borderRadius: 0, flexShrink: 0 }}>
                <Stack direction="row" alignItems="center" spacing={1}>
                    <IconButton onClick={() => navigate('/forms/employee-warning-notice/list')} size="small">
                        <ArrowBackIcon />
                    </IconButton>
                    <Typography variant="h6" sx={{ fontSize: '1rem' }}>Employee Warning Notice</Typography>
                    {saving && <CircularProgress size={20} />}
                </Stack>
            </Paper>

            <Box sx={{ p: 1.5, flexGrow: 1 }}>
                {submitted && <Alert severity="info" sx={{ mb: 1 }}>Form submitted - read-only</Alert>}
                {errorMsg && <Alert severity="error" sx={{ mb: 1 }} onClose={() => setErrorMsg('')}>{errorMsg}</Alert>}
                {success && <Alert severity="success" sx={{ mb: 1 }} onClose={() => setSuccess('')}>{success}</Alert>}

                <Paper sx={{ p: 1.5 }}>
                    {/* Date & Employee */}
                    <Grid container spacing={1} sx={{ mb: 1 }}>
                        <Grid item xs={6}>
                            <TextField
                                label="Date"
                                type="date"
                                value={form.date}
                                onChange={(e) => updateField('date', e.target.value)}
                                size="small"
                                fullWidth
                                disabled={submitted}
                                InputLabelProps={{ shrink: true }}
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField
                                label="Employee Name"
                                value={form.employeeName}
                                size="small"
                                fullWidth
                                disabled={submitted}
                                required
                                error={!!errors.employeeName}
                                helperText={errors.employeeName}
                                onClick={() => !submitted && setEmployeeModalOpen(true)}
                                InputProps={{
                                    readOnly: true,
                                    endAdornment: !submitted && (
                                        <InputAdornment position="end">
                                            <IconButton size="small" onClick={() => setEmployeeModalOpen(true)}>
                                                <PersonSearchIcon />
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                    sx: { cursor: submitted ? 'default' : 'pointer' }
                                }}
                            />
                        </Grid>
                    </Grid>

                    {/* Warning Reasons */}
                    <Typography variant="caption" fontWeight="600" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                        YOU ARE HEREBY WARNED ABOUT:
                    </Typography>
                    {errors.warningReasons && <Typography variant="caption" color="error">{errors.warningReasons}</Typography>}
                    <FormGroup sx={{ mb: 1 }}>
                        <Grid container spacing={0.5}>
                            {WARNING_REASONS.map((reason) => (
                                <Grid item xs={6} sm={4} key={reason.key}>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={!!form.warningReasons[reason.key]}
                                                onChange={(e) => {
                                                    const updated = {
                                                        ...form,
                                                        warningReasons: { ...form.warningReasons, [reason.key]: e.target.checked }
                                                    };
                                                    setForm(updated);
                                                    queueSave(updated);
                                                }}
                                                disabled={submitted}
                                                size="small"
                                            />
                                        }
                                        label={<Typography variant="caption">{reason.label}</Typography>}
                                        sx={{ m: 0 }}
                                    />
                                </Grid>
                            ))}
                        </Grid>
                    </FormGroup>

                    {/* Details */}
                    <TextField
                        label="DETAILS OF EVENT"
                        value={form.detailsOfEvent}
                        onChange={(e) => updateField('detailsOfEvent', e.target.value)}
                        size="small"
                        fullWidth
                        multiline
                        rows={2}
                        required
                        error={!!errors.detailsOfEvent}
                        helperText={errors.detailsOfEvent}
                        disabled={submitted}
                        sx={{ mb: 1 }}
                        InputProps={{
                            endAdornment: speechSupported && !submitted && (
                                <InputAdornment position="end">
                                    <IconButton
                                        size="small"
                                        onClick={() => {
                                            if (isListening && dictationTarget === 'detailsOfEvent') stopFieldDictation();
                                            else startFieldDictation('detailsOfEvent');
                                        }}
                                        color={isListening && dictationTarget === 'detailsOfEvent' ? "error" : "default"}
                                    >
                                        {isListening && dictationTarget === 'detailsOfEvent' ? <MicOffIcon /> : <MicIcon />}
                                    </IconButton>
                                </InputAdornment>
                            )
                        }}
                    />

                    {/* Warning Number */}
                    {errors.warningNumber && <Typography variant="caption" color="error">{errors.warningNumber}</Typography>}
                    <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
                        <FormControlLabel
                            control={<Checkbox checked={!!form.warningNumber.warning1} onChange={(e) => {
                                const updated = { ...form, warningNumber: { ...form.warningNumber, warning1: e.target.checked } };
                                setForm(updated); queueSave(updated);
                            }} disabled={submitted} size="small" />}
                            label={<Typography variant="caption">WARNING #1</Typography>}
                            sx={{ m: 0 }}
                        />
                        <FormControlLabel
                            control={<Checkbox checked={!!form.warningNumber.warning2} onChange={(e) => {
                                const updated = { ...form, warningNumber: { ...form.warningNumber, warning2: e.target.checked } };
                                setForm(updated); queueSave(updated);
                            }} disabled={submitted} size="small" />}
                            label={<Typography variant="caption">WARNING #2</Typography>}
                            sx={{ m: 0 }}
                        />
                        <FormControlLabel
                            control={<Checkbox checked={!!form.warningNumber.warning3} onChange={(e) => {
                                const updated = { ...form, warningNumber: { ...form.warningNumber, warning3: e.target.checked } };
                                setForm(updated); queueSave(updated);
                            }} disabled={submitted} size="small" />}
                            label={<Typography variant="caption">WARNING #3</Typography>}
                            sx={{ m: 0 }}
                        />
                    </Stack>

                    {/* Action Taken */}
                    <TextField
                        label="ACTION TAKEN"
                        value={form.actionTaken}
                        onChange={(e) => updateField('actionTaken', e.target.value)}
                        size="small"
                        fullWidth
                        multiline
                        rows={2}
                        required
                        error={!!errors.actionTaken}
                        helperText={errors.actionTaken}
                        disabled={submitted}
                        sx={{ mb: 1 }}
                        InputProps={{
                            endAdornment: speechSupported && !submitted && (
                                <InputAdornment position="end">
                                    <IconButton
                                        size="small"
                                        onClick={() => {
                                            if (isListening && dictationTarget === 'actionTaken') stopFieldDictation();
                                            else startFieldDictation('actionTaken');
                                        }}
                                        color={isListening && dictationTarget === 'actionTaken' ? "error" : "default"}
                                    >
                                        {isListening && dictationTarget === 'actionTaken' ? <MicOffIcon /> : <MicIcon />}
                                    </IconButton>
                                </InputAdornment>
                            )
                        }}
                    />

                    {/* Warning Text */}
                    <Box sx={{ mb: 1, p: 1, backgroundColor: '#fff3e0', borderRadius: 1, border: '1px solid #ff9800' }}>
                        <Typography variant="caption" fontWeight="600" color="error" sx={{ display: 'block', mb: 0.5 }}>
                            FURTHER REPRIMANDS MAY RESULT IN YOUR IMMEDIATE DISMISSAL!
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            Your signature is an acknowledgement that you have been warned.
                            If you disagree with the above warning, you may write the reasons for your objection below.
                        </Typography>
                    </Box>

                    {/* Objection */}
                    <TextField
                        label="Employee Objection (Optional)"
                        value={form.objectionText}
                        onChange={(e) => updateField('objectionText', e.target.value)}
                        size="small"
                        fullWidth
                        multiline
                        rows={2}
                        disabled={submitted}
                        sx={{ mb: 1 }}
                        InputProps={{
                            endAdornment: speechSupported && !submitted && (
                                <InputAdornment position="end">
                                    <IconButton
                                        size="small"
                                        onClick={() => {
                                            if (isListening && dictationTarget === 'objectionText') stopFieldDictation();
                                            else startFieldDictation('objectionText');
                                        }}
                                        color={isListening && dictationTarget === 'objectionText' ? "error" : "default"}
                                    >
                                        {isListening && dictationTarget === 'objectionText' ? <MicOffIcon /> : <MicIcon />}
                                    </IconButton>
                                </InputAdornment>
                            )
                        }}
                    />

                    {/* Photos Section */}
                    <Box sx={{ mb: 1 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                            <Typography variant="caption" fontWeight="600" color="text.secondary">
                                PHOTOS {(form.photos || []).length > 0 && `(${form.photos.length}/5)`}
                            </Typography>
                            {!submitted && (
                                <Button
                                    variant="outlined"
                                    size="small"
                                    startIcon={<CameraAltIcon />}
                                    onClick={() => setCameraOpen(true)}
                                    disabled={(form.photos || []).length >= 5}
                                    sx={{ fontSize: '0.7rem', py: 0.25 }}
                                >
                                    Add Photo
                                </Button>
                            )}
                        </Box>
                        {(form.photos || []).length > 0 && (
                            <Grid container spacing={1}>
                                {form.photos.map((photo, index) => (
                                    <Grid item xs={6} sm={4} key={index}>
                                        <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
                                            <Box
                                                component="img"
                                                src={photo.dataUrl || photo.url}
                                                alt={`Photo ${index + 1}`}
                                                sx={{ width: '100%', maxHeight: 150, objectFit: 'cover', display: 'block' }}
                                            />
                                            <Box sx={{ p: 0.5 }}>
                                                <TextField
                                                    fullWidth
                                                    size="small"
                                                    label="Description"
                                                    value={photo.description || ''}
                                                    onChange={(e) => updatePhotoDescription(index, e.target.value)}
                                                    disabled={submitted}
                                                    multiline
                                                    maxRows={2}
                                                    sx={{ '& .MuiInputBase-input': { fontSize: '0.75rem' } }}
                                                />
                                                {!submitted && (
                                                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 0.25 }}>
                                                        <IconButton size="small" color="error" onClick={() => removePhoto(index)}>
                                                            <DeleteIcon fontSize="small" />
                                                        </IconButton>
                                                    </Box>
                                                )}
                                            </Box>
                                        </Paper>
                                    </Grid>
                                ))}
                            </Grid>
                        )}
                    </Box>

                    {/* Signatures */}
                    <Grid container spacing={1}>
                        <Grid item xs={6}>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>Supervisor</Typography>
                            <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'flex-start' }}>
                                <Box
                                    sx={{
                                        flex: 1,
                                        border: '1px solid #ccc',
                                        borderRadius: 1,
                                        height: 60,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        backgroundColor: '#fafafa',
                                        cursor: submitted ? 'default' : 'pointer',
                                        ...(errors.supervisorSignature && { borderColor: 'error.main' })
                                    }}
                                    onClick={() => !submitted && setSupervisorSigModalOpen(true)}
                                >
                                    {form.supervisorSignature ? (
                                        <img src={form.supervisorSignature} alt="Supervisor" style={{ maxWidth: '100%', maxHeight: '100%' }} />
                                    ) : (
                                        <DrawIcon sx={{ color: '#999', fontSize: 20 }} />
                                    )}
                                </Box>
                                {form.supervisorSignature && !submitted && (
                                    <IconButton size="small" onClick={() => updateField('supervisorSignature', '')}>
                                        <ClearIcon fontSize="small" />
                                    </IconButton>
                                )}
                            </Box>
                            {form.supervisorSignatureDate && (
                                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                                    {dayjs(form.supervisorSignatureDate).format('MM/DD/YY HH:mm')}
                                </Typography>
                            )}
                            {errors.supervisorSignature && <Typography variant="caption" color="error">{errors.supervisorSignature}</Typography>}
                        </Grid>
                        <Grid item xs={6}>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>Employee</Typography>
                            <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'flex-start' }}>
                                <Box
                                    sx={{
                                        flex: 1,
                                        border: '1px solid #ccc',
                                        borderRadius: 1,
                                        height: 60,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        backgroundColor: '#fafafa',
                                        cursor: submitted ? 'default' : 'pointer',
                                        ...(errors.employeeSignature && { borderColor: 'error.main' })
                                    }}
                                    onClick={() => !submitted && setEmployeeSigModalOpen(true)}
                                >
                                    {form.employeeSignature ? (
                                        <img src={form.employeeSignature} alt="Employee" style={{ maxWidth: '100%', maxHeight: '100%' }} />
                                    ) : (
                                        <DrawIcon sx={{ color: '#999', fontSize: 20 }} />
                                    )}
                                </Box>
                                {form.employeeSignature && !submitted && (
                                    <IconButton size="small" onClick={() => updateField('employeeSignature', '')}>
                                        <ClearIcon fontSize="small" />
                                    </IconButton>
                                )}
                            </Box>
                            {form.employeeSignatureDate && (
                                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                                    {dayjs(form.employeeSignatureDate).format('MM/DD/YY HH:mm')}
                                </Typography>
                            )}
                            {errors.employeeSignature && <Typography variant="caption" color="error">{errors.employeeSignature}</Typography>}
                        </Grid>
                    </Grid>

                    {/* Submit */}
                    <Box sx={{ mt: 1.5, display: 'flex', justifyContent: 'center' }}>
                        <Button
                            variant="contained"
                            size="medium"
                            onClick={handleSubmit}
                            disabled={submitted || saving}
                            startIcon={saving ? <CircularProgress size={16} /> : <SendIcon />}
                            fullWidth
                        >
                            {saving ? 'Submitting...' : 'Submit Form'}
                        </Button>
                    </Box>
                </Paper>
            </Box>

            {/* Modals */}
            <SelectEmployeesAndTempsModal
                open={employeeModalOpen}
                onClose={() => setEmployeeModalOpen(false)}
                onSelect={handleEmployeeSelect}
                alreadySelected={form.employeeName ? [{ FullName: form.employeeName, _type: form.employeeType }] : []}
                singleSelect={true}
            />

            <SignatureModalWithAuto
                open={supervisorSigModalOpen}
                onClose={() => setSupervisorSigModalOpen(false)}
                sigPadRef={supervisorSigPadRef}
                employeeName="Supervisor"
                onAccept={acceptSupervisorSignature}
                existingSignature={form.supervisorSignature}
            />

            <SignatureModalWithAuto
                open={employeeSigModalOpen}
                onClose={() => setEmployeeSigModalOpen(false)}
                sigPadRef={employeeSigPadRef}
                employeeName={form.employeeName || 'Employee'}
                onAccept={acceptEmployeeSignature}
                existingSignature={form.employeeSignature}
            />

            <CameraCapture
                open={cameraOpen}
                onClose={() => setCameraOpen(false)}
                onCapture={handlePhotoCapture}
            />
        </Box>
    );
}
